import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import {
  FaDrum, FaCheckCircle, FaClock, FaUserGraduate,
  FaStar, FaArrowRight, FaPlay
} from 'react-icons/fa';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.15 } } };

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "Do I need any prior music experience to join the beginner tabla course?", "acceptedAnswer": { "@type": "Answer", "text": "No. This course is designed for absolute beginners. We start from the very basics — zero experience required." } },
    { "@type": "Question", "name": "What equipment do I need to start learning tabla?", "acceptedAnswer": { "@type": "Answer", "text": "A basic student tabla set (bayan + dayan). We'll guide you on what to buy. Online students can start with a practice tabla pad." } },
    { "@type": "Question", "name": "How long is each tabla lesson?", "acceptedAnswer": { "@type": "Answer", "text": "Each live session is 45–60 minutes. You'll also get recorded lessons to practise in between." } },
    { "@type": "Question", "name": "Can I take tabla classes online?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! We offer live one-on-one online classes via Zoom for students worldwide." } },
    { "@type": "Question", "name": "What language are tabla classes taught in?", "acceptedAnswer": { "@type": "Answer", "text": "Classes are conducted in Hindi, Marathi, or English depending on your preference." } }
  ]
};

const courseSchema = {
  "@context": "https://schema.org",
  "@type": "Course",
  "name": "Beginner Tabla Course — Learn Tabla from Scratch",
  "description": "A structured beginner tabla course taught by Maestro Atharva Kagalkar. Learn the fundamentals of Indian classical tabla from zero experience — no prior music knowledge needed.",
  "provider": {
    "@type": "MusicSchool",
    "@id": "https://www.taalsetu.com/#organization",
    "name": "Taalsetu Music Academy",
    "sameAs": "https://www.taalsetu.com"
  },
  "instructor": {
    "@type": "Person",
    "name": "Atharva Kagalkar",
    "jobTitle": "Tabla Maestro"
  },
  "url": "https://www.taalsetu.com/courses/beginner/",
  "image": "https://www.taalsetu.com/logo512.png",
  "courseMode": ["online", "onsite"],
  "educationalLevel": "Beginner",
  "inLanguage": "hi-IN",
  "availableLanguage": ["Hindi", "English", "Marathi"],
  "offers": {
    "@type": "Offer",
    "category": "Paid",
    "availability": "https://schema.org/InStock"
  },
  "hasCourseInstance": {
    "@type": "CourseInstance",
    "courseMode": "online",
    "instructor": { "@type": "Person", "name": "Atharva Kagalkar" }
  }
};

const curriculum = [
  { week: "Week 1–2", topic: "Introduction to Tabla", desc: "Parts of the tabla, how to sit correctly, basic hand positions and bols (Da, Dha, Ge, Ti)." },
  { week: "Week 3–4", topic: "Basic Bols & Strokes", desc: "Mastering individual strokes — Na, Ta, Ti, Te, Ke — with proper finger technique and resonance." },
  { week: "Week 5–6", topic: "Teentaal Introduction", desc: "Learning India's most popular taal (16 beats). Clapping, counting, and playing basic theka." },
  { week: "Week 7–8", topic: "Kaida & Simple Compositions", desc: "Your first kaida (theme-and-variations exercise) to develop speed, control, and muscle memory." },
  { week: "Week 9–10", topic: "Rhythm & Lay", desc: "Understanding laya (tempo) — vilambit (slow), madhya (medium). Playing with a metronome/tanpura." },
  { week: "Week 11–12", topic: "First Performance Piece", desc: "Performing a complete composition from start to finish — your graduation recital piece." },
];

const faqs = [
  { q: "Do I need any prior music experience?", a: "No. This course is designed for absolute beginners. We start from the very basics — zero experience required." },
  { q: "What equipment do I need?", a: "A basic student tabla set (bayan + dayan). We'll guide you on what to buy. Online students can start with a practice tabla pad." },
  { q: "How long is each lesson?", a: "Each live session is 45–60 minutes. You'll also get recorded lessons to practise in between." },
  { q: "Can I take classes online?", a: "Yes! We offer live one-on-one online classes via Zoom for students worldwide." },
  { q: "What language are classes taught in?", a: "Classes are conducted in Hindi, Marathi, or English depending on your preference." },
];

const BeginnerCourse = () => {
  return (
    <div className="min-h-screen font-sans bg-white dark:bg-maroon-dark">
      <SEO
        title="Beginner Tabla Classes Online — Learn Tabla from Scratch | Taalsetu"
        description="Start your tabla journey with Taalsetu's beginner tabla course. Learn tabla online from scratch with Maestro Atharva Kagalkar. No prior music experience needed. Online & Pune classes available."
        keywords="tabla classes for beginners, learn tabla from scratch, beginner tabla online, tabla lessons for beginners, how to learn tabla, tabla basic course, tabla classes Pune, Taalsetu beginner"
        url="https://www.taalsetu.com/courses/beginner/"
        schemaMarkup={[courseSchema, faqSchema]}
        breadcrumbs={[
          { name: 'Home', url: 'https://www.taalsetu.com/' },
          { name: 'Beginner Tabla Course', url: 'https://www.taalsetu.com/courses/beginner/' }
        ]}
      />

      {/* Hero */}
      <motion.section
        className="relative pt-28 pb-20 bg-gradient-to-br from-maroon via-maroon to-maroon-dark text-white overflow-hidden"
        initial="hidden" animate="visible" variants={fadeUp}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-10 w-48 h-48 bg-gold rounded-full blur-3xl" />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-flex items-center bg-gold/20 border border-gold/30 text-gold px-4 py-2 rounded-full text-sm font-medium mb-6">
            <FaDrum className="mr-2" /> Beginner Level
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold leading-tight mb-6">
            Learn Tabla Online —<br />
            <span className="text-gold">Start from Scratch</span>
          </h1>
          <p className="text-lg md:text-xl text-gold-light/80 max-w-2xl mb-10 leading-relaxed">
            A complete 3-month beginner tabla course with Maestro Atharva Kagalkar. Whether you're in Pune or anywhere in the world — start your tabla journey today.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="btn-gold flex items-center !rounded-xl text-lg px-8 py-4">
              Enrol Now <FaArrowRight className="ml-2" />
            </Link>
            <a href="tel:+918275664288" className="flex items-center px-8 py-4 border border-gold/40 text-gold-light rounded-xl hover:bg-gold/10 transition-all duration-300 font-medium text-lg">
              Call for Free Trial
            </a>
          </div>

          <div className="flex flex-wrap gap-8 mt-12 text-sm text-gold-light/60">
            {[
              { icon: <FaClock />, label: "3 Month Course" },
              { icon: <FaUserGraduate />, label: "No Experience Needed" },
              { icon: <FaStar />, label: "One-on-One Classes" },
              { icon: <FaPlay />, label: "Online & Offline" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-gold-light/70">
                <span className="text-gold">{item.icon}</span> {item.label}
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* What you'll learn */}
      <motion.section
        className="py-20 bg-white dark:bg-maroon-dark"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-maroon dark:text-gold-light mb-4">
              What You'll Learn
            </h2>
            <div className="gold-divider mb-4" />
            <p className="text-maroon/60 dark:text-gold-light/60 max-w-2xl mx-auto">
              A structured curriculum built for beginners — covering everything from your first bol to your first full composition.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Correct tabla posture and hand position",
              "All fundamental bols — Da, Dha, Ge, Ti, Na, Ta, Te, Ke",
              "Teentaal — India's most popular 16-beat rhythm cycle",
              "Simple kaida (theme-and-variations compositions)",
              "Understanding laya (tempo) and tala (rhythm cycle)",
              "Reading and writing tabla notation (bhatkhande)",
              "Practising with tanpura and metronome",
              "Perform a complete composition within 3 months",
            ].map((point, i) => (
              <motion.div key={i} variants={fadeUp} className="flex items-start gap-3">
                <FaCheckCircle className="text-gold mt-1 flex-shrink-0 text-lg" />
                <span className="text-maroon/70 dark:text-gold-light/70">{point}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Curriculum */}
      <motion.section
        className="py-20 bg-maroon/5 dark:bg-maroon-dark/50"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-maroon dark:text-gold-light mb-4">
              12-Week Curriculum
            </h2>
            <div className="gold-divider mb-4" />
          </motion.div>
          <div className="space-y-4">
            {curriculum.map((item, i) => (
              <motion.div
                key={i} variants={fadeUp}
                className="bg-white dark:bg-maroon-dark border border-gold/10 rounded-xl p-6 flex gap-6 hover:border-gold/30 transition-all duration-300"
              >
                <div className="w-28 flex-shrink-0">
                  <span className="text-xs font-bold text-gold uppercase tracking-wider">{item.week}</span>
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-maroon dark:text-gold-light mb-1">{item.topic}</h3>
                  <p className="text-sm text-maroon/60 dark:text-gold-light/60 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* FAQs */}
      <motion.section
        className="py-20 bg-white dark:bg-maroon-dark"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" variants={fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-maroon dark:text-gold-light mb-4">
              Frequently Asked Questions
            </h2>
            <div className="gold-divider" />
          </motion.div>
          <div className="space-y-5">
            {faqs.map((faq, i) => (
              <motion.div key={i} variants={fadeUp} className="border border-gold/15 rounded-xl p-6 bg-maroon/3 dark:bg-white/5">
                <h3 className="font-serif font-bold text-maroon dark:text-gold-light mb-2">{faq.q}</h3>
                <p className="text-maroon/65 dark:text-gold-light/65 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Further Reading */}
      <motion.section
        className="py-16 bg-white dark:bg-maroon-dark border-t border-gold/10"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-bold text-maroon dark:text-gold-light mb-6">Further Reading</h2>
          <Link
            to="/blog/learn-tabla-online-beginners/"
            className="group flex flex-col sm:flex-row gap-4 bg-maroon/4 dark:bg-white/5 border border-gold/15 rounded-xl p-6 hover:border-gold/40 transition-all duration-300"
          >
            <div className="flex-shrink-0 w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center text-gold">
              <FaDrum />
            </div>
            <div>
              <p className="text-xs font-bold text-gold uppercase tracking-wider mb-1">Beginners Guide</p>
              <p className="font-serif font-bold text-maroon dark:text-gold-light group-hover:text-gold transition-colors">How to Learn Tabla Online as a Complete Beginner</p>
              <p className="text-sm text-maroon/60 dark:text-gold-light/60 mt-1">Equipment, practice routines, how to find the right teacher, and what to expect in your first 3 months.</p>
            </div>
          </Link>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        className="py-20 bg-maroon text-white text-center"
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      >
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Ready to Begin Your Tabla Journey?</h2>
          <p className="text-gold-light/70 mb-8 text-lg">Join students from India, USA, UK, UAE and beyond who started with zero experience.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register" className="btn-gold !rounded-xl px-10 py-4 text-lg flex items-center">
              Enrol Now <FaArrowRight className="ml-2" />
            </Link>
            <Link to="/#contact" className="px-10 py-4 border border-gold/40 text-gold-light rounded-xl hover:bg-gold/10 transition-all duration-300 text-lg">
              Contact Us
            </Link>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default BeginnerCourse;
